import {
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  SelectChangeEvent,
  Stack,
  Theme,
  useTheme,
} from '@mui/material';
import Typography from '@mui/material/Typography';
import { get as _get } from 'lodash';
import React from 'react';
import { useSelector } from 'react-redux';
import { getMakes, getModels } from '../../../api/vehicleApi';
import { getAuthKey, getSessionId, getUrlKey } from '../../../app/store/combinedSelectors';
import { GenericProductType } from '../../../types/type';
import { getValidVehicleYears } from '../../../utils/Utilities';
import ModalLinearActivityIndicator from '../../shared/ModalLinearActivityIndicator/ModalLinearActivityIndicator';
import { FigmaCarIcon, FigmaPropertyIcon } from '../../shared/Svg-icons/App-Icons';

export default function RiskOptionList(props: any) {
  const theme: Theme = useTheme();
  const years = getValidVehicleYears();

  const [modalLoadingOpen, setModalLoadingOpen] = React.useState(false);
  const [showVehiclePicker, setShowVehiclePicker] = React.useState(false);
  const [selectedYear, setSelectedYear] = React.useState('');
  const [selectedMake, setSelectedMake] = React.useState('');
  const [selectedModel, setSelectedModel] = React.useState('');
  const [selectedIndex, setSelectedIndex] = React.useState(0);
  const [makesList, setMakesList] = React.useState<string[]>([]);
  const [modelsList, setModelsList] = React.useState<string[]>([]);

  const urlKey = useSelector(getUrlKey);
  const sessionId = useSelector(getSessionId);
  const authKey = useSelector(getAuthKey);

  const filteredAutoRisks = props.risks.filter((risk: any) => risk.generalizedProductType === GenericProductType.Auto);

  function handleChange(change: any) {
    const selectedIndex: number = +_get(change, 'target.value', '-1');
    setSelectedIndex(selectedIndex);
    if (selectedIndex === -2) {
      setShowVehiclePicker(true);
      props.handleRiskNotOnPolicy();
    } else {
      setShowVehiclePicker(false);
      const selectedRisk = props.risks[selectedIndex];
      props.setRisk(selectedRisk);
    }
  }

  async function handleYearChange(event: SelectChangeEvent) {
    setMakesList([]);
    setModelsList([]);
    setSelectedMake('');
    setSelectedModel('');
    const selectedYear: string = _get(event, 'target.value', '-1');
    setSelectedYear(selectedYear);
    const makes = await getMakes(urlKey, sessionId, authKey, selectedYear);
    setMakesList(makes);
  }

  async function handleMakeChange(change: any) {
    const selectedMake: string = _get(change, 'target.value', '-1');
    setSelectedMake(selectedMake);
    const models = await getModels(urlKey, sessionId, authKey, selectedYear.toString(), selectedMake);
    setModelsList(models);
  }

  function handleModelChange(change: any) {
    const selectedModel: string = _get(change, 'target.value', '-1');
    setSelectedModel(selectedModel);

    if (filteredAutoRisks.length > 0) {
      const vehicleNotOnPolicy = Object.assign({}, filteredAutoRisks[0], {
        id: '-1',
        year: `${selectedYear}`,
        make: `${selectedMake}`,
        model: `${selectedModel}`,
        vin: '',
        vehicleTypeName: 'Vehicle is not on policy',
      });
      props.setRisk(vehicleNotOnPolicy);
    }
  }

  return (
    <Grid
      container
      sx={{
        width: '100%',
        maxWidth: '1280px',
        minWidth: '310px',
        margin: '0 auto',
        marginTop: '1em',
      }}
    >
      {modalLoadingOpen && <ModalLinearActivityIndicator />}

      <Grid item sx={{ marginBottom: '.5em' }} xs={12}>
        <Typography
          sx={{
            ...theme.custom.styles.titleType4,
          }}
        >
          Which item needs a claim?
        </Typography>
      </Grid>

      <FormControl
        sx={{
          width: '100%',
        }}
      >
        <RadioGroup
          aria-labelledby="demo-radio-buttons-group-label"
          defaultValue={0}
          name="radio-buttons-group"
          sx={{ width: '100%' }}
          onChange={handleChange}
        >
          {props.risks &&
            props.risks.map((risk: any, index: number) => (
              <Paper
                key={index}
                variant="outlined"
                sx={{
                  mt: '.8rem',
                  px: '15px',
                  py: '0.5rem',
                  minHeight: theme.custom.styles.defaultInputHeight,
                  borderRadius: theme.custom.styles.borderRadius.radiusDefault,
                  border:
                    theme.custom.styles.showBorderAroundListItems && selectedIndex === index
                      ? `2px solid ${theme.custom.styles.radioandcheckboxStyle.selectedBorder}`
                      : theme.custom.styles.showThickBorderForUnselectedListItem
                        ? `2px solid ${theme.custom.styles.radioandcheckboxStyle.defaultBorder}`
                        : `1px solid ${theme.custom.styles.radioandcheckboxStyle.defaultBorder}`,
                }}
              >
                <FormControlLabel
                  key={index}
                  sx={{
                    width: '100%',
                    borderRadius: '4px',
                    alignItems: 'flex-start',
                  }}
                  value={index}
                  control={
                    <Radio
                      sx={{
                        paddingTop: 0,
                        paddingBottom: 0,
                        marginTop: '0.25rem',
                        '& svg': {
                          width: theme.custom.styles.defaultRadioButtonSize,
                          height: theme.custom.styles.defaultRadioButtonSize,
                        },
                        color: theme.custom.colors.radioButtonColor,
                        '&.Mui-checked': {
                          color: theme.custom.colors.radioButtonSelectedColor,
                        },
                      }}
                    />
                  }
                  label={
                    <Stack
                      direction="row"
                      sx={{
                        alignItems: 'flex-start',
                      }}
                    >
                      {theme.custom.styles.showIconOnRisks &&
                        risk.generalizedProductType === GenericProductType.Auto && (
                          <FigmaCarIcon theme={theme}></FigmaCarIcon>
                        )}
                      {theme.custom.styles.showIconOnRisks &&
                        risk.generalizedProductType !== GenericProductType.Auto && (
                          <FigmaPropertyIcon theme={theme}></FigmaPropertyIcon>
                        )}

                      <Stack direction="column">
                        <Typography>{risk.description}</Typography>
                        {risk.vin && (
                          <Typography>
                            VIN:{' '}
                            <span data-dd-privacy="mask" data-dd-action-name="VIN">
                              {risk.vin}
                            </span>
                          </Typography>
                        )}
                        <Typography sx={theme.custom.styles.body2}>Policy #{risk.policyNumber} </Typography>
                      </Stack>
                    </Stack>
                  }
                />
              </Paper>
            ))}

          {filteredAutoRisks.length > 0 && (
            <Paper
              variant="outlined"
              sx={{
                marginTop: '1rem',
                px: '15px',
                display: 'flex',
                borderRadius: theme.custom.styles.borderRadius.radiusDefault,
                height: theme.custom.styles.defaultInputHeight,
                border:
                  theme.custom.styles.showBorderAroundListItems && selectedIndex === -2
                    ? `2px solid ${theme.custom.styles.radioandcheckboxStyle.selectedBorder}`
                    : theme.custom.styles.showThickBorderForUnselectedListItem
                      ? `2px solid ${theme.custom.styles.radioandcheckboxStyle.defaultBorder}`
                      : `1px solid ${theme.custom.styles.radioandcheckboxStyle.defaultBorder}`,
              }}
            >
              <FormControlLabel
                sx={{
                  width: '100%',
                  borderRadius: '4px',
                }}
                value={-2}
                control={
                  <Radio
                    sx={{
                      paddingTop: 0,
                      paddingBottom: 0,
                      '& svg': {
                        width: theme.custom.styles.defaultRadioButtonSize,
                        height: theme.custom.styles.defaultRadioButtonSize,
                      },
                      color: theme.custom.colors.radioButtonColor,
                      '&.Mui-checked': {
                        color: theme.custom.colors.radioButtonSelectedColor,
                      },
                    }}
                  />
                }
                label={
                  <Stack
                    direction="row"
                    sx={{
                      alignItems: 'center',
                      height: theme.custom.styles.defaultInputHeight,
                    }}
                  >
                    {theme.custom.styles.showIconOnRisks && <FigmaCarIcon theme={theme}></FigmaCarIcon>}

                    <Stack direction="column">
                      <Typography variant="body1">Vehicle is not on Policy</Typography>
                    </Stack>
                  </Stack>
                }
              />
            </Paper>
          )}
        </RadioGroup>
      </FormControl>

      <Grid sx={{ marginTop: '3em' }} item xs={12}>
        <Typography>If you don't see the item listed, please contact us for assistance.</Typography>
      </Grid>

      {showVehiclePicker && (
        <Grid sx={{ marginTop: '2em' }} container>
          <Grid item sx={{ marginTop: '1em', marginBottom: '.5em' }} xs={12}>
            <Typography
              sx={{
                ...theme.custom.styles.titleType4,
              }}
            >
              Enter the vehicle's year, make, and model.
            </Typography>
          </Grid>
          <Grid item container spacing={2}>
            <Grid sx={{ marginTop: '1em' }} item xs={12} sm={4}>
              <FormControl
                fullWidth
                sx={{
                  '& .MuiInputLabel-root:not(.MuiInputLabel-shrink)': {
                    transform: 'translate(14px, 9px) scale(1) !important',
                    paddingTop: '0 !important',
                  },
                }}
              >
                <InputLabel id="demo-simple-select-label1">Vehicle Year</InputLabel>
                <Select
                  onChange={handleYearChange}
                  value={selectedYear}
                  label="Vehicle Year"
                  MenuProps={{
                    disablePortal: true,
                  }}
                  sx={{
                    '.MuiOutlinedInput-notchedOutline': {
                      borderWidth: theme.custom.styles.defaultBorderWidth,
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderWidth: theme.custom.styles.defaultBorderWidth,
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderWidth: theme.custom.styles.defaultBorderWidth,
                    },
                  }}
                >
                  {years &&
                    years.map((year: number) => (
                      <MenuItem value={year} key={year}>
                        {year}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid sx={{ marginTop: '1em' }} item xs={12} sm={4}>
              <FormControl
                fullWidth
                sx={{
                  '& .MuiInputLabel-root:not(.MuiInputLabel-shrink)': {
                    transform: 'translate(14px, 9px) scale(1) !important',
                    paddingTop: '0 !important',
                  },
                }}
              >
                <InputLabel id="demo-simple-select-label2">Vehicle Make</InputLabel>
                <Select
                  value={selectedMake}
                  label="Vehicle Make"
                  onChange={handleMakeChange}
                  disabled={!selectedYear}
                  MenuProps={{
                    disablePortal: true,
                  }}
                  sx={{
                    '.MuiOutlinedInput-notchedOutline': {
                      borderWidth: theme.custom.styles.defaultBorderWidth,
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderWidth: theme.custom.styles.defaultBorderWidth,
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderWidth: theme.custom.styles.defaultBorderWidth,
                    },
                  }}
                >
                  {makesList &&
                    makesList.map((make: string) => (
                      <MenuItem value={make} key={make}>
                        {make}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid sx={{ marginTop: '1em' }} item xs={12} sm={4}>
              <FormControl
                fullWidth
                size="medium"
                sx={{
                  '& .MuiInputLabel-root:not(.MuiInputLabel-shrink)': {
                    transform: 'translate(14px, 9px) scale(1) !important',
                    paddingTop: '0 !important',
                  },
                }}
              >
                <InputLabel id="demo-simple-select-label3">Vehicle Model</InputLabel>
                <Select
                  onChange={handleModelChange}
                  value={selectedModel}
                  label="Vehicle Model"
                  disabled={!selectedMake}
                  MenuProps={{
                    disablePortal: true,
                  }}
                  sx={{
                    '.MuiOutlinedInput-notchedOutline': {
                      borderWidth: theme.custom.styles.defaultBorderWidth,
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderWidth: theme.custom.styles.defaultBorderWidth,
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderWidth: theme.custom.styles.defaultBorderWidth,
                    },
                  }}
                >
                  {modelsList &&
                    modelsList.map((model: string) => (
                      <MenuItem value={model} key={model}>
                        {model}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Grid>
      )}
    </Grid>
  );
}
